/**
 * QuickAddModal — bottom-sheet with full TaskDetail for rapid task creation.
 *
 * A persistent draft task lives in Dexie at `parentPath: "root/#quick-add"`
 * with `status: Hidden`. This ensures:
 *  - The TipTap/Yjs editor works (Y.Doc stored per task ID)
 *  - Unsaved edits persist across sessions
 *  - The draft never appears in any view
 *
 * On Save the draft is moved to the chosen container (default: Inbox)
 * and promoted to `status: Unfinished`. A fresh draft is created next
 * time Quick Add opens.
 */

import {
  memo,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react"
import { useTranslation } from "react-i18next"
import RotateCcw from "@/shared/icons/pikaicons/stroke/arrows-chevrons/rotate-left.svg?react"
import KeyboardChevronDown from "@/shared/icons/pikaicons/stroke/devices/keyboard-chevron-down.svg?react"
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
} from "@/app/components/ui/drawer"
import { ObjectType } from "@/db/schema"
import { completeTask, uncompleteTask } from "@/db/operations"
import { hapticLight, hapticMedium } from "@/shared/utils/platform/haptics"
import { TaskDetail } from "@/features/task-detail/TaskDetail"
import { ContainerPicker } from "@/features/task-detail/components/ContainerPicker"
import { StaticIcon } from "@/shared/icons/StaticIcon"
import { DynamicIcon } from "@/features/navigation/DynamicIcon"
import { cn } from "@/shared/utils/general/cn"
import { Capacitor } from "@capacitor/core"
import { useKeyboardStore } from "@/features/auth/stores/keyboardStore"
import { useSetting } from "@/features/settings/hooks/useSetting"
import { useStaticDataImmediate } from "@/features/static-data/useStaticData"
import {
  QUICK_ADD_FOLDER_SETTING_ID,
  QUICK_ADD_FOLDER_DEFAULT,
} from "@/features/settings/config/components/QuickAddFolderSelect"
import { useQuickAddDraft } from "./useQuickAddDraft"

interface QuickAddModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export const QuickAddModal = memo(function QuickAddModal({
  open,
  onOpenChange,
}: QuickAddModalProps) {
  const { t } = useTranslation()
  const { draft, save, resetDraft } = useQuickAddDraft(open)

  // Quick Add default folder from settings
  const { value: defaultFolder } = useSetting(
    QUICK_ADD_FOLDER_SETTING_ID,
    QUICK_ADD_FOLDER_DEFAULT,
  )
  const [staticData] = useStaticDataImmediate()

  const [containerId, setContainerId] = useState<string>("#inbox")
  const [containerTitle, setContainerTitle] = useState("Inbox")
  const [containerPickerOpen, setContainerPickerOpen] = useState(false)

  const contentRef = useRef<HTMLDivElement>(null)
  const hasFocusedRef = useRef(false)
  const keyboardOpen = useKeyboardStore((s) => s.keyboard?.isOpen ?? false)
  const keyboardAnimating = useKeyboardStore(
    (s) => s.keyboard !== null && !s.keyboard.isOpen,
  )
  const keyboardHeight = useKeyboardStore((s) => s.keyboard?.height ?? 0)

  // Resolve container metadata for the bottom bar icon
  const containerMeta = useMemo(() => {
    if (containerId === "#inbox") return { virtual: "inbox" as const }
    if (containerId === "#anyday") return { virtual: "anyday" as const }
    const item = staticData?.allMenuItems?.find((m) => m.id === containerId)
    return item ? { item } : { virtual: "inbox" as const }
  }, [containerId, staticData?.allMenuItems])

  // Apply default folder from settings each time the modal opens
  useEffect(() => {
    if (!open) return
    if (defaultFolder === "inbox" || defaultFolder === QUICK_ADD_FOLDER_DEFAULT) {
      setContainerId("#inbox")
      setContainerTitle(t("Inbox"))
    } else if (defaultFolder === "anyday") {
      setContainerId("#anyday")
      setContainerTitle(t("Anyday"))
    } else {
      // Real container ID — look up title from static data.
      // Falls back to Inbox if the project was completed / deleted.
      const item = staticData?.allMenuItems?.find((m) => m.id === defaultFolder)
      if (item) {
        setContainerId(item.id)
        setContainerTitle(item.title)
      } else {
        setContainerId("#inbox")
        setContainerTitle(t("Inbox"))
      }
    }
  }, [open, defaultFolder, staticData?.allMenuItems, t])

  // Switch to KeyboardResize.None while drawer is open so that the
  // WKWebView stays full-size and Vaul's window-resize listener never
  // recalculates snap-points (which caused the flash/disappear bug).
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return
    const { setResizeMode } = useKeyboardStore.getState()
    import("@capacitor/keyboard").then(({ Keyboard, KeyboardResize }) => {
      if (open) {
        setResizeMode("none")
        void Keyboard.setResizeMode({ mode: KeyboardResize.None })
      } else {
        setResizeMode("native")
        void Keyboard.setResizeMode({ mode: KeyboardResize.Native })
      }
    }).catch(() => {})
  }, [open])

  // Reset focus tracking when drawer closes
  useEffect(() => {
    if (!open) {
      hasFocusedRef.current = false
    }
  }, [open])

  // Focus title once after drawer opens (not on every draft update)
  useEffect(() => {
    if (!open || !draft || hasFocusedRef.current) return
    const timer = setTimeout(() => {
      const el = contentRef.current?.querySelector<HTMLElement>(
        "[contenteditable][aria-label]",
      )
      if (el && !el.contains(document.activeElement)) {
        el.focus()
        const range = document.createRange()
        const sel = window.getSelection()
        range.selectNodeContents(el)
        range.collapse(false)
        sel?.removeAllRanges()
        sel?.addRange(range)
      }
      hasFocusedRef.current = true
    }, 50)
    return () => clearTimeout(timer)
  }, [open, draft])

  const handleSave = useCallback(async () => {
    hapticMedium()
    const ok = await save(containerId)
    if (ok) {
      onOpenChange(false)
    }
  }, [containerId, save, onOpenChange])

  const handleClose = useCallback(() => {
    onOpenChange(false)
  }, [onOpenChange])

  const handleComplete = useCallback((id: string) => {
    void completeTask(id)
  }, [])

  const handleUncomplete = useCallback((id: string) => {
    void uncompleteTask(id)
  }, [])

  const handleContainerSelect = useCallback(
    (id: string, name: string) => {
      setContainerId(id)
      setContainerTitle(name)
      setContainerPickerOpen(false)
      hapticLight()
    },
    [],
  )

  return (
    <>
      <Drawer
        open={open}
        onOpenChange={(v) => {
          // Don't close while ContainerPicker is open
          if (!v && containerPickerOpen) return
          onOpenChange(v)
        }}
        noBodyStyles
      >
        <DrawerContent
          className="vaul-fast mx-auto max-w-lg max-h-[85vh] flex flex-col"
          style={{ paddingBottom: (keyboardOpen || keyboardAnimating) ? keyboardHeight : 0 }}
        >
          <DrawerTitle className="sr-only">{t("Quick Add")}</DrawerTitle>

          {/* Full TaskDetail — scrollable area */}
          {draft ? (
            <div ref={contentRef} className="flex-1 overflow-y-auto min-h-0">
              <TaskDetail
                task={draft}
                editTitle
                onComplete={handleComplete}
                onUncomplete={handleUncomplete}
                onClose={handleClose}
              />
            </div>
          ) : (
            <div className="flex-1 p-4 text-muted-foreground">Loading draft...</div>
          )}

          {/* Bottom bar: container picker + reset + save */}
          <div
            className="flex items-center justify-between border-t px-4 py-3 shrink-0"
            style={{ paddingBottom: (keyboardOpen || keyboardAnimating) ? undefined : "calc(12px + var(--safe-area-bottom, 0px))" }}
          >
            <button
              type="button"
              onClick={() => setContainerPickerOpen(true)}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
            >
              <ContainerBarIcon meta={containerMeta} />
              <span>{containerTitle}</span>
            </button>

            <div className="flex items-center gap-2">
              {/* Dismiss keyboard — only on native when keyboard is visible */}
              {(keyboardOpen || keyboardAnimating) && Capacitor.isNativePlatform() && (
                <button
                  type="button"
                  onClick={() => {
                    import("@capacitor/keyboard").then(({ Keyboard }) => {
                      void Keyboard.hide()
                    }).catch(() => {})
                  }}
                  className={cn(
                    "rounded-full p-2 text-sm font-medium",
                    "border border-border text-muted-foreground",
                    "hover:bg-accent transition-colors active:scale-95",
                  )}
                  aria-label={t("Dismiss keyboard")}
                >
                  <KeyboardChevronDown className="size-4" />
                </button>
              )}

              {/* Reset draft — starts fresh */}
              <button
                type="button"
                onClick={() => {
                  hapticLight()
                  void resetDraft()
                }}
                className={cn(
                  "rounded-full px-4 py-2 text-sm font-medium",
                  "border border-border text-muted-foreground",
                  "hover:bg-accent transition-colors active:scale-95",
                )}
              >
                <RotateCcw className="size-4" />
              </button>

              <button
                type="button"
                onClick={() => void handleSave()}
                className={cn(
                  "rounded-full px-6 py-2 text-sm font-semibold",
                  "bg-primary text-primary-foreground",
                  "transition-transform active:scale-95",
                )}
              >
                {t("Save")}
              </button>
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      <ContainerPicker
        open={containerPickerOpen}
        onOpenChange={setContainerPickerOpen}
        onSelect={handleContainerSelect}
        currentContainerId={containerId}
        showVirtualContainers
      />
    </>
  )
})

// ---------------------------------------------------------------------------
// Bottom bar icon — shows the right icon for the current container
// ---------------------------------------------------------------------------

type ContainerBarMeta =
  | { virtual: "inbox" | "anyday" }
  | { item: import("@/features/static-data/useStaticData").MenuItem }

function ContainerBarIcon({ meta }: { meta: ContainerBarMeta }) {
  if ("virtual" in meta) {
    return <StaticIcon type={meta.virtual} size={16} mode="color" active />
  }
  const { item } = meta
  if (item.type === ObjectType.Project || item.type === ObjectType.List) {
    const pct =
      item.totalCount > 0
        ? Math.round((item.completedCount / item.totalCount) * 100)
        : 0
    return (
      <DynamicIcon
        icon={item.icon}
        type={item.type === ObjectType.Project ? "project" : "list"}
        percentage={pct}
        unfinished={item.unfinishedCount}
        status={item.status}
        size="small"
      />
    )
  }
  return <StaticIcon type="area" size={20} mode="color" />
}
